/**
 * Skill install path, agent detection, and write-error helpers.
 *
 * The command runner performs template lookup and filesystem writes; this
 * module owns the pure-ish install support logic that is easiest to test in
 * isolation.
 */

import { existsSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import { command } from "./brand.js";

export type AgentType = "claude" | "codex" | "copilot";

export const SUPPORTED_AGENTS: AgentType[] = ["claude", "codex", "copilot"];

type DetectAgentOptions = {
  cwd?: string;
  env?: NodeJS.ProcessEnv;
  exists?: (path: string) => boolean;
};

type InstallPathOptions = {
  cwd?: string;
  home?: string;
};

export function isAgentType(value: string): value is AgentType {
  return value === "claude" || value === "codex" || value === "copilot";
}

/**
 * Detect which AI agent is currently running based on environment variables
 * and project directory structure.
 */
export function detectAgent(options: DetectAgentOptions = {}): AgentType | null {
  const env = options.env ?? process.env;
  const cwd = options.cwd ?? process.cwd();
  const pathExists = options.exists ?? existsSync;

  if (env.CLAUDE) return "claude";

  if (
    env.CODEX ||
    env.CODEX_CI ||
    env.CODEX_THREAD_ID ||
    env.CODEX_MANAGED_PACKAGE_ROOT ||
    env.AGENT_MODE === "codex"
  )
    return "codex";

  if (
    env.GITHUB_COPILOT ||
    env.COPILOT_AGENT ||
    env.AGENT_MODE === "copilot"
  )
    return "copilot";

  const foundAgents = SUPPORTED_AGENTS.filter((agent) =>
    pathExists(join(cwd, agent === "copilot" ? ".github" : `.${agent}`))
  );

  if (foundAgents.length === 1) return foundAgents[0] ?? null;
  return null;
}

/**
 * Resolve the install directory for a skill based on agent type.
 */
export function resolveInstallDir(
  skillName: string,
  agent: AgentType,
  personal: boolean,
  options: InstallPathOptions = {}
): string {
  const base = personal
    ? (options.home ?? homedir())
    : (options.cwd ?? process.cwd());

  switch (agent) {
    case "claude":
      return join(base, ".claude", "skills", skillName);
    case "codex":
      return join(base, ".codex", "skills", skillName);
    case "copilot":
      return personal
        ? join(base, ".copilot", "skills", skillName)
        : join(base, ".github", "skills", skillName);
  }
}

export type InstallWriteFailureContext = {
  cwd: string;
  templateName: string;
  installName: string;
  agent: AgentType;
  personal: boolean;
  dir: string;
  filePath: string;
  error: unknown;
};

function errorField(error: unknown, key: string): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const value = (error as Record<string, unknown>)[key];
  return typeof value === "string" ? value : undefined;
}

export function isInstallPermissionError(error: unknown): boolean {
  const code = errorField(error, "code");
  return code === "EACCES" || code === "EPERM" || code === "EROFS";
}

function formatErrorSummary(error: unknown): string {
  const code = errorField(error, "code");
  const message = errorField(error, "message");
  if (code && message) return `${code}: ${message}`;
  if (message) return message;
  if (error instanceof Error) return error.message;
  return String(error);
}

export function formatInstallWriteFailure(
  context: InstallWriteFailureContext
): string {
  const lines = [
    `Could not install ${context.templateName} skill for ${context.agent} at ${context.filePath}.`,
    "",
    isInstallPermissionError(context.error)
      ? "The destination is not writable from the current shell."
      : "The CLI could not create or write the destination file.",
    `Failure: ${formatErrorSummary(context.error)}`,
    "",
  ];
  const nameFlag =
    context.installName === context.templateName
      ? ""
      : ` --name ${context.installName}`;

  if (context.personal) {
    lines.push(
      `Personal installs write under your home directory. Check permissions for ${context.dir}.`,
      "",
      "Try again after fixing permissions:",
      `  ${command} skill install ${context.templateName}${nameFlag} --agent ${context.agent} --personal`,
      "",
      "Or run from a writable project directory and install without --personal."
    );
  } else {
    lines.push(
      `Project installs write under the current directory (${context.cwd}).`,
      "Run from a writable project folder, or use --personal to install under your home directory.",
      "",
      "Try:",
      "  cd <writable-project>",
      `  ${command} skill install ${context.templateName}${nameFlag} --agent ${context.agent}`,
      `  ${command} skill install ${context.templateName}${nameFlag} --agent ${context.agent} --personal`
    );
  }

  return lines.join("\n");
}

/**
 * A custom install name becomes a directory segment and the frontmatter
 * `name:`, so it must be a single safe path component: word characters and
 * hyphens only, no separators, no `..`, no leading dot.
 */
export function isValidInstallName(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(value);
}
